import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import ReactMarkdown from 'react-markdown';
import { X, ThumbsUp, ThumbsDown, Send, Check, Bot, User, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

const FeedbackBar = ({ conversationId, isOpen, onToggle, messages = [], preSelectedMessage = null, initialRating = null, selectedMessageIndex = null }) => {
    const [feedback, setFeedback] = useState('');
    const [rating, setRating] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [selectedBotMessages, setSelectedBotMessages] = useState([]);
    const messageListRef = useRef(null);

    // Handle pre-selected message and rating when feedback bar is opened
    useEffect(() => {
        if (preSelectedMessage !== null && messages.length > 0) {
            const messageIndex = selectedMessageIndex ?? messages.findIndex(msg => 
                msg.message === preSelectedMessage.message && 
                msg.sender === preSelectedMessage.sender
            );
            if (messageIndex !== -1 && messages[messageIndex].sender === 'bot') {
                setSelectedBotMessages([messageIndex]);
                setRating(preSelectedMessage.rating);
                
                setTimeout(() => {
                    const messageElements = messageListRef.current?.getElementsByClassName('message-item');
                    if (messageElements && messageElements[messageIndex]) {
                        messageElements[messageIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            }
        }
    }, [preSelectedMessage, messages, selectedMessageIndex]);

    // Reset form when feedback bar is closed
    useEffect(() => {
        if (!isOpen) {
            setFeedback('');
            setRating(null);
            setSelectedBotMessages([]);
            setSubmitStatus(null);
        }
    }, [isOpen]);

    const handleMessageSelect = (index) => {
        if (messages[index].sender === 'bot') {
            setSelectedBotMessages(prev => {
                const isSelected = prev.includes(index);
                return isSelected ? prev.filter(i => i !== index) : [...prev, index];
            });
        }
    };

    const isMessageHighlighted = (index, message) => {
        if (message.sender === 'bot') {
            return selectedBotMessages.includes(index);
        }
        return index < messages.length - 1 && 
               messages[index].sender === 'user' && 
               selectedBotMessages.includes(index + 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const feedbackMessages = selectedBotMessages.flatMap(botIndex => {
                const userMessage = botIndex > 0 && messages[botIndex - 1].sender === 'user' 
                    ? [{ message: messages[botIndex - 1].message, sender: messages[botIndex - 1].sender }] 
                    : [];
                return [...userMessage, { message: messages[botIndex].message, sender: messages[botIndex].sender }];
            });

            const feedbackData = {
                conversation_id: conversationId ? parseInt(conversationId) : null,
                is_main_chat: !conversationId,
                rating: rating || 5,
                feedback_text: feedback,
                selected_messages: feedbackMessages
            };

            await api.post('/feedback', feedbackData);
            setSubmitStatus('success');
            setFeedback('');
            setRating(null);
            setSelectedBotMessages([]);
            
            setTimeout(() => {
                onToggle();
            }, 1500);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={onToggle}
                />
            )}

            {/* Feedback Panel */}
            <div className={cn(
                "fixed top-0 right-0 h-full w-full sm:w-[420px] bg-background border-l shadow-xl z-50 flex flex-col transition-transform duration-300 ease-in-out",
                isOpen ? "translate-x-0" : "translate-x-full"
            )}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-lg">Provide Feedback</h3>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onToggle}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Rating Section */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-muted-foreground">How was this response?</label>
                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant={rating === 5 ? "default" : "outline"}
                                className={cn(
                                    "flex-1 h-12",
                                    rating === 5 && "bg-green-600 hover:bg-green-700"
                                )}
                                onClick={() => setRating(5)}
                            >
                                <ThumbsUp className="h-5 w-5 mr-2" />
                                Good
                            </Button>
                            <Button
                                type="button"
                                variant={rating === 1 ? "default" : "outline"}
                                className={cn(
                                    "flex-1 h-12",
                                    rating === 1 && "bg-red-600 hover:bg-red-700"
                                )}
                                onClick={() => setRating(1)}
                            >
                                <ThumbsDown className="h-5 w-5 mr-2" />
                                Bad
                            </Button>
                        </div>
                    </div>

                    {/* Message Selection */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-muted-foreground">
                            Select messages to include ({selectedBotMessages.length} selected)
                        </label>
                        <div className="rounded-lg border bg-muted/30 p-2">
                            <ScrollArea className="h-64" ref={messageListRef}>
                                <div className="space-y-2 pr-2">
                                    {messages.map((message, index) => (
                                        <div
                                            key={index}
                                            className={cn(
                                                "message-item flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all",
                                                message.sender === 'bot' ? "hover:bg-primary/5" : "opacity-70",
                                                isMessageHighlighted(index, message) && "bg-primary/10 border border-primary/30"
                                            )}
                                            onClick={() => message.sender === 'bot' && handleMessageSelect(index)}
                                        >
                                            {message.sender === 'bot' && (
                                                <div className={cn(
                                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5",
                                                    selectedBotMessages.includes(index) 
                                                        ? "bg-primary border-primary text-primary-foreground" 
                                                        : "border-muted-foreground/30"
                                                )}>
                                                    {selectedBotMessages.includes(index) && <Check className="h-3 w-3" />}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    {message.sender === 'bot' ? (
                                                        <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                                                    ) : (
                                                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                                                    )}
                                                    <span className="text-xs font-medium text-muted-foreground uppercase">
                                                        {message.sender === 'bot' ? 'AI' : 'You'}
                                                    </span>
                                                </div>
                                                <div className="text-sm line-clamp-3">
                                                    {message.sender === 'bot' ? (
                                                        <div className="prose prose-sm dark:prose-invert max-w-none">
                                                            <ReactMarkdown>{String(message.message || '').substring(0, 200) + (message.message && message.message.length > 200 ? '...' : '')}</ReactMarkdown>
                                                        </div>
                                                    ) : (
                                                        <p className="text-muted-foreground">{message.message}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>

                    {/* Feedback Text */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium text-muted-foreground">Additional comments</label>
                        <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Share your thoughts about this conversation..."
                            className="w-full h-32 px-4 py-3 rounded-lg border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            required
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t bg-card space-y-3">
                    {submitStatus === 'success' && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm">
                            <Check className="h-4 w-4" />
                            Thank you for your feedback!
                        </div>
                    )}
                    {submitStatus === 'error' && (
                        <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
                            Failed to submit feedback. Please try again.
                        </div>
                    )}
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !feedback.trim() || rating === null || selectedBotMessages.length === 0}
                        className="w-full h-12"
                    >
                        {isSubmitting ? (
                            <>
                                <span className="animate-spin mr-2">⏳</span>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-2" />
                                Submit Feedback
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default FeedbackBar;
